/* ============================================================
   WSH Header / Navigation — scroll + hover behavior
   Pairs with wsh-header.css. Doesn't fight Horizon's header.js.
   ============================================================ */

(function () {
  "use strict";

  const SCROLL_ON = 80;
  const SCROLL_OFF = 40;
  const wrapper = document.querySelector("[data-wsh-nav]");
  if (!wrapper) return;

  /* --------------------------------------------------
     Sticky / utility-bar-collapse with hysteresis.
     Activates at 80px, deactivates at 40px.
     The wide dead zone prevents the feedback loop where
     the utility-bar height collapse shifts scrollY back
     below the threshold.
     -------------------------------------------------- */
  let ticking = false;
  let lastScrolled = false;

  function updateScroll() {
    const y = window.scrollY;
    const scrolled = lastScrolled ? y > SCROLL_OFF : y > SCROLL_ON;
    if (scrolled !== lastScrolled) {
      wrapper.classList.toggle("is-scrolled", scrolled);
      lastScrolled = scrolled;
    }
    ticking = false;
  }

  window.addEventListener(
    "scroll",
    function () {
      if (!ticking) {
        window.requestAnimationFrame(updateScroll);
        ticking = true;
      }
    },
    { passive: true }
  );

  // Initial state
  updateScroll();

  /* --------------------------------------------------
     Observe live viewport height for sticky offset calcs
     (Horizon's header.js already handles --header-height;
      we just need to make sure our utility bar height is
      accounted for before scroll)
     -------------------------------------------------- */
  const utilityBar = wrapper.querySelector(".wsh-utility-bar");
  if (utilityBar) {
    const setUtilHeight = () => {
      const h = utilityBar.offsetHeight;
      wrapper.style.setProperty("--wsh-util-current-height", h + "px");
    };
    setUtilHeight();
    if (window.ResizeObserver) {
      new ResizeObserver(setUtilHeight).observe(utilityBar);
    }
  }

  /* --------------------------------------------------
     Mega-menu hover-delay (150ms)
     Horizon uses :hover for open. We add a small delay
     via data attribute to avoid accidental opens.
     -------------------------------------------------- */
  const HOVER_DELAY = 150;
  const megaItems = wrapper.querySelectorAll(
    ".menu-list__list-item:has(.menu-list__submenu)"
  );

  megaItems.forEach((item) => {
    let openTimer = null;

    item.addEventListener("pointerenter", () => {
      clearTimeout(openTimer);
      openTimer = setTimeout(() => {
        item.setAttribute("data-wsh-mega-open", "true");
      }, HOVER_DELAY);
    });

    item.addEventListener("pointerleave", () => {
      clearTimeout(openTimer);
      item.removeAttribute("data-wsh-mega-open");
    });
  });
  /* --------------------------------------------------
     Enhanced Search (§8.10)
     -------------------------------------------------- */
  const searchEl = document.querySelector("[data-wsh-search]");
  if (searchEl) initWSHSearch(searchEl);

  function initWSHSearch(root) {
    const input = root.querySelector("[data-wsh-search-input]");
    const clearBtn = root.querySelector("[data-wsh-search-clear]");
    const kbdEl = root.querySelector("[data-wsh-search-kbd]");
    const kbdMod = root.querySelector("[data-wsh-search-kbd-mod]");
    const chips = root.querySelectorAll("[data-wsh-chip]");
    const defaultPanel = root.querySelector("[data-wsh-search-default]");
    const livePanel = root.querySelector("[data-wsh-search-live]");
    const emptyPanel = root.querySelector("[data-wsh-search-empty]");
    const emptyQuery = root.querySelector("[data-wsh-search-empty-query]");
    const loadingPanel = root.querySelector("[data-wsh-search-loading]");
    const productsHead = root.querySelector("[data-wsh-search-products-head]");
    const productsList = root.querySelector("[data-wsh-search-products]");
    const collectionsHead = root.querySelector("[data-wsh-search-collections-head]");
    const collectionsList = root.querySelector("[data-wsh-search-collections]");
    const footer = root.querySelector("[data-wsh-search-footer]");
    const viewAllLink = root.querySelector("[data-wsh-search-view-all]");
    const closeBtns = root.querySelectorAll("[data-wsh-search-close]");

    let activeChip = "all";
    let debounceTimer = null;
    let lastQuery = "";
    let currentRequest = null;

    // Detect Mac for keyboard shortcut display
    if (kbdMod && navigator.platform && !/Mac/.test(navigator.platform)) {
      kbdMod.textContent = "Ctrl+";
    }

    /* ---- Open / Close ---- */
    function openSearch() {
      root.hidden = false;
      // Prevent body scroll
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => input.focus());
      renderRecent();
    }

    function closeSearch() {
      root.hidden = true;
      document.body.style.overflow = "";
      input.value = "";
      clearBtn.hidden = true;
      resetPanels();
    }

    function resetPanels() {
      defaultPanel.hidden = false;
      livePanel.hidden = true;
      emptyPanel.hidden = true;
      loadingPanel.hidden = true;
      footer.hidden = true;
    }

    // Close buttons (backdrop + cancel)
    closeBtns.forEach(function (btn) {
      btn.addEventListener("click", closeSearch);
    });

    // Escape key
    root.addEventListener("keydown", function (e) {
      if (e.key === "Escape") {
        e.preventDefault();
        closeSearch();
      }
    });

    // Intercept Horizon's search button — prevent its native modal
    function interceptSearchTrigger() {
      var searchBtns = document.querySelectorAll(
        'search-button button, [aria-label="Search"], .search-action button'
      );
      searchBtns.forEach(function (btn) {
        btn.addEventListener(
          "click",
          function (e) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            openSearch();
          },
          true
        );
      });
    }
    interceptSearchTrigger();
    // Re-intercept after Shopify section rendering (theme editor)
    document.addEventListener("shopify:section:load", interceptSearchTrigger);

    // Cmd/Ctrl+K shortcut
    document.addEventListener("keydown", function (e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (root.hidden) {
          openSearch();
        } else {
          closeSearch();
        }
      }
    });

    /* ---- Input handling ---- */
    input.addEventListener("input", function () {
      var q = input.value.trim();
      clearBtn.hidden = q.length === 0;

      // Hide kbd hint when typing
      if (kbdEl) kbdEl.hidden = q.length > 0;

      clearTimeout(debounceTimer);
      if (q.length === 0) {
        resetPanels();
        return;
      }

      debounceTimer = setTimeout(function () {
        performSearch(q);
      }, 250);
    });

    clearBtn.addEventListener("click", function () {
      input.value = "";
      clearBtn.hidden = true;
      if (kbdEl) kbdEl.hidden = false;
      resetPanels();
      input.focus();
    });

    // Enter key — go to search results page
    input.addEventListener("keydown", function (e) {
      if (e.key === "Enter") {
        var q = input.value.trim();
        if (q.length > 0) {
          saveRecent(q);
          window.location.href =
            "/search?q=" + encodeURIComponent(q) + getChipParam();
        }
      }
    });

    /* ---- Filter chips ---- */
    chips.forEach(function (chip) {
      chip.addEventListener("click", function () {
        chips.forEach(function (c) {
          c.classList.remove("is-active");
        });
        chip.classList.add("is-active");
        activeChip = chip.getAttribute("data-wsh-chip");

        // Re-search if there's a query
        var q = input.value.trim();
        if (q.length > 0) {
          performSearch(q);
        }
      });
    });

    function getChipParam() {
      if (activeChip === "all") return "";
      return "&type=product&q=" + encodeURIComponent(activeChip);
    }

    /* ---- Search API ---- */

    // Detect preview_theme_id for section rendering on unpublished themes
    var previewParam = "";
    try {
      var urlParams = new URLSearchParams(window.location.search);
      var previewId = urlParams.get("preview_theme_id");
      if (previewId) previewParam = "&preview_theme_id=" + previewId;
    } catch (e) { /* old browser */ }

    function performSearch(query) {
      lastQuery = query;

      // Show loading
      defaultPanel.hidden = true;
      livePanel.hidden = true;
      emptyPanel.hidden = true;
      loadingPanel.hidden = false;
      footer.hidden = true;

      // Cancel previous request
      if (currentRequest) currentRequest.abort();
      var controller = new AbortController();
      currentRequest = controller;

      // Try Section Rendering API first (gives us metafields + formatted prices)
      // Falls back to raw JSON if section not found
      var url =
        "/search/suggest?q=" +
        encodeURIComponent(query) +
        "&resources[type]=product,collection" +
        "&resources[limit]=8" +
        "&resources[options][unavailable_products]=hide" +
        "&section_id=wsh-search-results" +
        previewParam;

      fetch(url, { signal: controller.signal })
        .then(function (r) {
          if (!r.ok) {
            // Section not found — fall back to JSON endpoint
            return fetchRawJson(query, controller.signal);
          }
          return r.text().then(function (html) {
            var parsed = parseSectionJson(html);
            if (parsed) return parsed;
            // Section rendered but no JSON — fall back
            return fetchRawJson(query, controller.signal);
          });
        })
        .then(function (data) {
          currentRequest = null;
          renderResults(data, query);
        })
        .catch(function (err) {
          if (err.name !== "AbortError") {
            currentRequest = null;
            loadingPanel.hidden = true;
            emptyPanel.hidden = false;
            emptyQuery.textContent = query;
          }
        });
    }

    function fetchRawJson(query, signal) {
      var url =
        "/search/suggest.json?q=" +
        encodeURIComponent(query) +
        "&resources[type]=product,collection" +
        "&resources[limit]=8" +
        "&resources[options][unavailable_products]=hide";

      return fetch(url, { signal: signal })
        .then(function (r) { return r.json(); })
        .then(function (data) { return normalizeRawData(data); });
    }

    function normalizeRawData(data) {
      var resources = (data.resources || {}).results || {};
      return {
        products: (resources.products || []).map(function (p) {
          return {
            title: p.title,
            handle: p.handle,
            url: p.url,
            type: p.type,
            image: p.featured_image ? p.featured_image.url : (p.image || ""),
            image_alt: (p.featured_image || {}).alt || p.title,
            price: formatPriceRaw(p.price),
            compare_at_price: (p.compare_at_price_max && parseFloat(p.compare_at_price_max) > parseFloat(p.price))
              ? formatPriceRaw(p.compare_at_price_max) : null,
            soundbar_subtitle: "",
            tags: p.tags || []
          };
        }),
        collections: (resources.collections || []).map(function (c) {
          return { title: c.title, url: c.url, handle: c.handle, products_count: null };
        })
      };
    }

    function formatPriceRaw(val) {
      // Raw API returns price as a string like "20.00" or "95.00" in store currency (USD)
      if (!val) return "";
      var num = parseFloat(val);
      if (isNaN(num)) return val;
      // Use Shopify's currency info if available
      var symbol = "$";
      if (window.Shopify && window.Shopify.currency) {
        var c = window.Shopify.currency.active;
        var symbols = { USD: "$", EUR: "\u20ac", GBP: "\u00a3", CAD: "CA$", AUD: "A$" };
        symbol = symbols[c] || c + " ";
      }
      return symbol + num.toFixed(2).replace(/\.00$/, "");
    }

    function parseSectionJson(html) {
      // Extract JSON from the rendered section HTML
      try {
        var tmp = document.createElement("div");
        tmp.innerHTML = html;
        var jsonEl = tmp.querySelector("[data-wsh-search-json]");
        if (jsonEl) {
          return JSON.parse(jsonEl.textContent);
        }
      } catch (e) {
        /* parse failed — will use fallback */
      }
      return null;
    }

    function renderResults(data, query) {
      loadingPanel.hidden = true;

      var products = data.products || [];
      var collections = data.collections || [];

      // Filter by active chip (client-side)
      if (activeChip !== "all") {
        products = filterByChip(products, activeChip);
      }

      var hasProducts = products.length > 0;
      var hasCollections = collections.length > 0;

      if (!hasProducts && !hasCollections) {
        emptyPanel.hidden = false;
        emptyQuery.textContent = query;
        livePanel.hidden = true;
        footer.hidden = true;
        return;
      }

      // Save as recent
      saveRecent(query);

      // Render products — with soundbar_subtitle and Shopify-formatted price
      productsHead.textContent =
        "Products" + (hasProducts ? " \u00b7 " + products.length : "");
      productsList.innerHTML = products
        .map(function (p) {
          var img = p.image
            ? '<img src="' + escapeAttr(p.image) + '" alt="' + escapeAttr(p.image_alt || p.title) + '" loading="lazy">'
            : "";
          var subtitle = p.soundbar_subtitle || "";
          var price = p.price || "";
          var compare = p.compare_at_price
            ? '<span class="wsh-search__strike">' + escapeHtml(p.compare_at_price) + "</span>"
            : "";
          // WSH Bundle Price Display — outlined Save chip on bundle results.
          // Default chip size, same construction as homepage BYB / cards.
          var chip = (p.save_tier && p.save_tier > 0)
            ? '<span class="wsh-save-chip">SAVE ' + p.save_tier + '%</span>'
            : "";

          return (
            '<a href="' + escapeAttr(p.url) + '" class="wsh-search__product-row">' +
            '<div class="wsh-search__product-thumb">' +
            img +
            '<button type="button" class="wsh-search__product-play" data-wsh-play="' +
            escapeAttr(p.handle || "") +
            '" aria-label="Preview audio">' +
            '<svg viewBox="0 0 16 16" fill="currentColor"><polygon points="4,2 4,14 14,8"/></svg>' +
            "</button>" +
            "</div>" +
            '<div class="wsh-search__product-info">' +
            '<div class="wsh-search__product-name">' + escapeHtml(p.title) + "</div>" +
            (subtitle ? '<div class="wsh-search__product-subtitle">' + escapeHtml(subtitle) + "</div>" : "") +
            "</div>" +
            '<div class="wsh-search__product-price">' +
            escapeHtml(price) + compare + chip +
            "</div>" +
            "</a>"
          );
        })
        .join("");

      // Render collections — with product count
      collectionsHead.textContent =
        "Collections" + (hasCollections ? " \u00b7 " + collections.length : "");
      collectionsList.innerHTML = collections
        .map(function (c) {
          var countStr = typeof c.products_count === "number"
            ? '<span class="wsh-search__collection-count">' + c.products_count + " products</span>"
            : "";
          return (
            '<a href="' + escapeAttr(c.url) + '" class="wsh-search__collection-row">' +
            "<span>" + escapeHtml(c.title) + "</span>" +
            countStr +
            "</a>"
          );
        })
        .join("");

      // Show panels — Products + Collections at top, NO Popular/Recent at top
      livePanel.hidden = false;
      emptyPanel.hidden = true;

      // Footer
      footer.hidden = false;
      viewAllLink.href = "/search?q=" + encodeURIComponent(query);

      // Wire play buttons
      wirePlayButtons();
    }


    function filterByChip(products, chip) {
      // Map chip values to search terms for client-side filtering
      var chipMap = {
        country: ["country", "americana", "bluegrass", "western"],
        rock: ["rock", "alternative", "classic rock"],
        "jazz-blues": ["jazz", "blues"],
        indie: ["indie", "folk", "songwriter"],
        cinematic: ["cinematic", "ambient", "sfx"],
        pop: ["pop", "electro", "r&b"],
        drums: ["drum", "drums"],
        bundles: ["bundle", "bundles"],
        "logic-pro": ["logic"],
        "pro-tools": ["pro tools"],
        cubase: ["cubase"],
        garageband: ["garageband"],
        ableton: ["ableton"],
      };
      var terms = chipMap[chip] || [chip];

      return products.filter(function (p) {
        var haystack = (
          (p.title || "") +
          " " +
          (p.type || "") +
          " " +
          (p.tags || []).join(" ")
        ).toLowerCase();
        return terms.some(function (t) {
          return haystack.indexOf(t.toLowerCase()) > -1;
        });
      });
    }

    /* ---- Audio preview ---- */
    function wirePlayButtons() {
      var playBtns = root.querySelectorAll("[data-wsh-play]");
      playBtns.forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          var handle = btn.getAttribute("data-wsh-play");
          if (handle && window.WSHAudioManager) {
            window.WSHAudioManager.playPreview(handle);
          }
        });
      });
    }

    /* ---- Recent searches (localStorage) ---- */
    var RECENT_KEY = "wsh-recent-search";

    function getRecent() {
      try {
        return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
      } catch (e) {
        return [];
      }
    }

    function saveRecent(query) {
      var q = query.trim();
      if (!q) return;
      var recent = getRecent();
      recent = [q].concat(
        recent.filter(function (r) {
          return r.toLowerCase() !== q.toLowerCase();
        })
      );
      recent = recent.slice(0, 5);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(recent));
      } catch (e) {
        /* quota */
      }
    }

    function renderRecent() {
      renderRecentInto(root.querySelector("[data-wsh-search-recent]"));
    }

    function renderRecentInto(container) {
      if (!container) return;
      var recent = getRecent();
      if (recent.length === 0) {
        container.innerHTML =
          '<div class="wsh-search__empty-recent">No recent searches</div>';
        return;
      }
      container.innerHTML = recent
        .map(function (q) {
          return (
            '<a href="/search?q=' +
            encodeURIComponent(q) +
            '" class="wsh-search__recent-item">' +
            '<svg class="wsh-search__recent-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>' +
            escapeHtml(q) +
            "</a>"
          );
        })
        .join("");
    }

    /* ---- Helpers ---- */
    function escapeHtml(str) {
      if (!str) return "";
      var div = document.createElement("div");
      div.appendChild(document.createTextNode(str));
      return div.innerHTML;
    }

    function escapeAttr(str) {
      return str.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    }
  }
})();
